import type { ErrorEvent } from '@sentry/react';

import { isStaleChunkError, isStaleChunkRecovering } from '@/lib/chunkRecovery';

const isSentryEnabled = import.meta.env.VITE_ENABLE_SENTRY === 'true';
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const release = import.meta.env.VITE_APP_RELEASE;
const environment = import.meta.env.VITE_APP_ENV;

const isOwnFrame = ({ filename }: { filename?: string }) =>
  !!filename && filename.startsWith(window.location.origin);

// Sentry's own sentryWrapped frame sits on top of every event caught from a wrapped handler and
// is served from our origin, so it says nothing about where the error came from.
const isForeignError = (event: ErrorEvent) => {
  const frames = (event.exception?.values ?? [])
    .flatMap(({ stacktrace }) => stacktrace?.frames ?? [])
    .filter(({ function: functionName }) => functionName !== 'sentryWrapped');

  return frames.length > 0 && !frames.some(isOwnFrame);
};

export const initSentry = async () => {
  if (!isSentryEnabled || !sentryDsn) return;

  const Sentry = await import('@sentry/react');

  Sentry.init({
    dsn: sentryDsn,
    release,
    environment,
    tracePropagationTargets: ['localhost', /^https:\/\/api\.agioratings\.io\//],
    dataCollection: { userInfo: true },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        triggerLabel: 'Send Feedback',
        formTitle: 'Feedback',
        submitButtonLabel: 'Send',
      }),
    ],
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend: event => {
      const isChunkError = event.exception?.values?.some(({ value }) =>
        isStaleChunkError(value ?? ''),
      );

      if (isChunkError && isStaleChunkRecovering()) return null;

      return isForeignError(event) ? null : event;
    },
  });
};
